// Roteamento (TanStack Router, code-based — sem codegen, evita conflito com `tsc --noEmit` do build).
// rootRoute = só providers de UI (sem chrome). Abaixo: /auth fullscreen e o layout _app (sidebar)
// que envolve as páginas internas.
import { lazy, Suspense } from "react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { authClient, getAppAccess } from "@/lib/auth-client";
import { canAccess } from "./lib/access.js";
import { appGuardDecision } from "./lib/app-guard.js";
import { lerBuscaAssinatura } from "./lib/subscription-search.js";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { useApplyTheme } from "./theme.js";
import { useApplyLanguage } from "./language.js";
import { AppErrorScreen, AppUpdateWatcher } from "./lib/app-updater.js";
import { AnalyticsListeners } from "./lib/analytics-listeners.js";
import { AppLayout } from "./app-layout.js";
import { Dashboard } from "./dashboard.js";
import { EmptyScreen } from "./empty-screen.js";
import i18n from "./i18n.js";
import { CapabilityDetail } from "./capability-detail.js";
import { SupportLayout } from "./support-layout.js";
import { LoginScreen } from "./login-screen.js";
import type { CrmStep } from "./crm-connection-screen.js";
import type { IntegrationProvider } from "./api.js";
import { PhoneWorkspaceSkeleton } from "./softphone/phone-notices.js";
import {
  AccessLogSkeleton,
  ActiveCallsSkeleton,
  AiConstructorRouteSkeleton,
  AiEvaluationsSkeleton,
  AiScriptsSkeleton,
  BillingSkeleton,
  CallFormsSkeleton,
  CallHealthSkeleton,
  CallsReportSkeleton,
  ClickAuditSkeleton,
  ClickHealthSkeleton,
  ConversionStagesSkeleton,
  DispositionsReportSkeleton,
  FullscreenRouteSkeleton,
  LeadsSkeleton,
  McpKeysSkeleton,
  PaymentSkeleton,
  ProfileSkeleton,
  QueueConfigSkeleton,
  QueueReportSkeleton,
  QueuesSkeleton,
  RouteScreenSkeleton,
  SellersReportSkeleton,
  SettingsDispositionsSkeleton,
  SettingsSkeleton,
  SipAgentsSkeleton,
  TempCallRouteSkeleton,
  TrunksSkeleton,
  UraSkeleton,
  UsersSkeleton,
  VoicesSkeleton,
  WebhookLogsSkeleton,
} from "@/components/screen-skeletons";

// ── Telas sob demanda ────────────────────────────────────────────────────────
// Toda tela de rota é lazy: o bundle inicial carrega só o que a PRIMEIRA tela
// precisa (o layout com a sidebar, o dashboard e o login) e cada rota busca a
// sua ao navegar. Antes disto o arquivo importava as ~84 telas estaticamente e
// um vendedor que só abre o discador baixava junto a configuração de fila, os
// usuários, o faturamento e o compliance.
//
// O `import()` com o `.then` que renomeia o export nomeado para `default` é o
// formato que o bundler precisa ver estaticamente para emitir um chunk por
// tela — não troque por um `import()` calculado.
//
// Ficam ESTÁTICAS (acima): `AppLayout` e `Dashboard`, que são o caminho crítico
// do boot — carregá-las sob demanda só somaria um round-trip antes do primeiro
// pixel; `LoginScreen`, primeira tela de quem chega sem sessão (o guard do _app
// redireciona para /auth antes de qualquer outra coisa); `SupportLayout`
// (9 linhas, só um <Outlet />), `EmptyScreen` e `CapabilityDetail`, pequenos
// demais para justificar uma fronteira de chunk; e os imports de TIPO, que não
// geram código.
//
// O fallback do Suspense NUNCA é `null`: enquanto o chunk baixa, a área de
// conteúdo ficaria em branco. Cada rota entrega o esqueleto da própria tela
// (screen-skeletons.tsx) dentro da moldura de página.
//
// A assinatura devolve o PRÓPRIO tipo da tela (`T`), então quem monta a rota
// continua checado nas props (`<SupportTicketScreen id={id} />`). Por dentro os
// dois `as` são inevitáveis: as props de `T` só existem no chamador, e aqui só
// precisamos repassá-las intactas.
type ScreenProps = Record<string, unknown>;

function lazyScreen<T extends ComponentType<never>>(
  carregar: () => Promise<{ default: T }>,
  Esqueleto?: ComponentType,
): T {
  const Screen = lazy(
    carregar as unknown as () => Promise<{ default: ComponentType<ScreenProps> }>,
  );
  function LazyScreen(props: ScreenProps) {
    return (
      <Suspense
        fallback={
          <RouteScreenSkeleton>{Esqueleto ? <Esqueleto /> : undefined}</RouteScreenSkeleton>
        }
      >
        <Screen {...props} />
      </Suspense>
    );
  }
  return LazyScreen as unknown as T;
}

// Mesma ideia para as telas fullscreen (fora do _app): não há sidebar nem
// cabeçalho de página para reservar, então a moldura do fallback é outra.
function lazyFullscreenScreen<T extends ComponentType<never>>(
  carregar: () => Promise<{ default: T }>,
): T {
  const Screen = lazy(
    carregar as unknown as () => Promise<{ default: ComponentType<ScreenProps> }>,
  );
  function LazyFullscreenScreen(props: ScreenProps) {
    return (
      <Suspense fallback={<FullscreenRouteSkeleton />}>
        <Screen {...props} />
      </Suspense>
    );
  }
  return LazyFullscreenScreen as unknown as T;
}

// Autenticação e primeiro acesso (fullscreen). Todas são alcançadas por um
// clique a partir do login, onde uma espera curta já é esperada.
const SignupScreen = lazyFullscreenScreen(() =>
  import("./signup-screen.js").then((m) => ({ default: m.SignupScreen })),
);
const ForgotPasswordScreen = lazyFullscreenScreen(() =>
  import("./forgot-password-screen.js").then((m) => ({ default: m.ForgotPasswordScreen })),
);
const ResetPasswordScreen = lazyFullscreenScreen(() =>
  import("./reset-password-screen.js").then((m) => ({ default: m.ResetPasswordScreen })),
);
const ChangePasswordRequiredScreen = lazyFullscreenScreen(() =>
  import("./change-password-required-screen.js").then((m) => ({
    default: m.ChangePasswordRequiredScreen,
  })),
);
const OnboardingOrganizationScreen = lazyFullscreenScreen(() =>
  import("./onboarding-organization-screen.js").then((m) => ({
    default: m.OnboardingOrganizationScreen,
  })),
);
const SubscriptionGateScreen = lazyFullscreenScreen(() =>
  import("./subscription-gate-screen.js").then((m) => ({ default: m.SubscriptionGateScreen })),
);
const TermsScreen = lazyFullscreenScreen(() =>
  import("./legal-screen.js").then((m) => ({ default: m.TermsScreen })),
);
const PrivacyScreen = lazyFullscreenScreen(() =>
  import("./legal-screen.js").then((m) => ({ default: m.PrivacyScreen })),
);

// Lazy: o discador arrasta o SIP.js (pesado) — só carrega ao abrir /dialer,
// mantendo o bundle inicial enxuto.
const DialerScreen = lazy(() =>
  import("./dialer-screen.js").then((m) => ({ default: m.DialerScreen })),
);
// Construtor de roteiro (/management/ai-script/construtor/$id): tela cheia com o
// canvas do pacote @script-builder. Lazy — arrasta xyflow + dnd-kit, que não
// devem pesar o bundle inicial de quem nunca abre o construtor.
const AiScriptConstrutorScreen = lazy(() =>
  import("./ai-script-construtor-screen.js").then((m) => ({
    default: m.AiScriptConstrutorScreen,
  })),
);
// Acesso temporário para ligar (/call): tela pública, lazy pela mesma razão
// (arrasta o SIP.js) — só carrega quando alguém abre o link.
const TempCallScreen = lazy(() =>
  import("./temp-call-screen.js").then((m) => ({ default: m.TempCallScreen })),
);

// Telas internas (dentro do _app).
const ControlDesk = lazyScreen(() =>
  import("./control-desk.js").then((m) => ({ default: m.ControlDesk })),
);
const HelpScreen = lazyScreen(() =>
  import("./help-screen.js").then((m) => ({ default: m.HelpScreen })),
);
const SupportTicketsScreen = lazyScreen(() =>
  import("./support-tickets-screen.js").then((m) => ({ default: m.SupportTicketsScreen })),
);
const SupportTicketScreen = lazyScreen(() =>
  import("./support-ticket-screen.js").then((m) => ({ default: m.SupportTicketScreen })),
);
const JourneyScreen = lazyScreen(() =>
  import("./journey-screen.js").then((m) => ({ default: m.JourneyScreen })),
);
const Sobre = lazyScreen(() => import("./sobre.js").then((m) => ({ default: m.Sobre })));

const SipAgentsScreen = lazyScreen(
  () => import("./sip-agents-screen.js").then((m) => ({ default: m.SipAgentsScreen })),
  SipAgentsSkeleton,
);
const TrunksScreen = lazyScreen(
  () => import("./trunks-screen.js").then((m) => ({ default: m.TrunksScreen })),
  TrunksSkeleton,
);
const ActiveCallsScreen = lazyScreen(
  () => import("./active-calls-screen.js").then((m) => ({ default: m.ActiveCallsScreen })),
  ActiveCallsSkeleton,
);
const QueuesScreen = lazyScreen(
  () => import("./queues-screen.js").then((m) => ({ default: m.QueuesScreen })),
  QueuesSkeleton,
);

const CallsReportScreen = lazyScreen(
  () => import("./calls-report-screen.js").then((m) => ({ default: m.CallsReportScreen })),
  CallsReportSkeleton,
);
const ReportsOverviewScreen = lazyScreen(() =>
  import("./reports-overview-screen.js").then((m) => ({ default: m.ReportsOverviewScreen })),
);
const PredictiveReportScreen = lazyScreen(
  () =>
    import("./predictive-report-screen.js").then((m) => ({ default: m.PredictiveReportScreen })),
  CallsReportSkeleton,
);
const CallHealthScreen = lazyScreen(
  () => import("./call-health-screen.js").then((m) => ({ default: m.CallHealthScreen })),
  CallHealthSkeleton,
);
const QueueReportScreen = lazyScreen(
  () => import("./queue-report-screen.js").then((m) => ({ default: m.QueueReportScreen })),
  QueueReportSkeleton,
);
const CadenceReportScreen = lazyScreen(
  () => import("./cadence-report-screen.js").then((m) => ({ default: m.CadenceReportScreen })),
  CallsReportSkeleton,
);
const DispositionsReportScreen = lazyScreen(
  () =>
    import("./dispositions-report-screen.js").then((m) => ({
      default: m.DispositionsReportScreen,
    })),
  DispositionsReportSkeleton,
);
const SellersReportScreen = lazyScreen(
  () => import("./sellers-report-screen.js").then((m) => ({ default: m.SellersReportScreen })),
  SellersReportSkeleton,
);
const AllBrandsReportScreen = lazyScreen(
  () => import("./all-brands-report-screen.js").then((m) => ({ default: m.AllBrandsReportScreen })),
  SellersReportSkeleton,
);

const UsersScreen = lazyScreen(
  () => import("./users-screen.js").then((m) => ({ default: m.UsersScreen })),
  UsersSkeleton,
);
const RolesScreen = lazyScreen(
  () => import("./roles-screen.js").then((m) => ({ default: m.RolesScreen })),
  UsersSkeleton,
);
const AccessLogScreen = lazyScreen(
  () => import("./access-log-screen.js").then((m) => ({ default: m.AccessLogScreen })),
  AccessLogSkeleton,
);
const ComplianceScreen = lazyScreen(() =>
  import("./compliance-screen.js").then((m) => ({ default: m.ComplianceScreen })),
);
const WebhookLogsScreen = lazyScreen(
  () => import("./webhook-logs-screen.js").then((m) => ({ default: m.WebhookLogsScreen })),
  WebhookLogsSkeleton,
);

const ClickToCallOverviewScreen = lazyScreen(() =>
  import("./click-to-call-overview-screen.js").then((m) => ({
    default: m.ClickToCallOverviewScreen,
  })),
);
const ClickToCallAuditScreen = lazyScreen(
  () =>
    import("./click-to-call-audit-screen.js").then((m) => ({ default: m.ClickToCallAuditScreen })),
  ClickAuditSkeleton,
);
// Reexporta o Relatório de Ligações com `source="clickToCall"`: sem o lazy, esta
// rota arrastaria a tela de relatório inteira para o bundle inicial.
const ClickToCallReportScreen = lazyScreen(
  () =>
    import("./click-to-call-report-screen.js").then((m) => ({
      default: m.ClickToCallReportScreen,
    })),
  CallsReportSkeleton,
);
const ClickToCallHealthScreen = lazyScreen(
  () =>
    import("./click-to-call-health-screen.js").then((m) => ({
      default: m.ClickToCallHealthScreen,
    })),
  ClickHealthSkeleton,
);

const CallFormScreen = lazyScreen(
  () => import("./call-form-screen.js").then((m) => ({ default: m.CallFormScreen })),
  CallFormsSkeleton,
);
const AiEvaluationsScreen = lazyScreen(
  () => import("./ai-evaluations-screen.js").then((m) => ({ default: m.AiEvaluationsScreen })),
  AiEvaluationsSkeleton,
);
const LeadsScreen = lazyScreen(
  () => import("./leads-screen.js").then((m) => ({ default: m.LeadsScreen })),
  LeadsSkeleton,
);
const SalesScriptScreen = lazyScreen(() =>
  import("./sales-script-screen.js").then((m) => ({ default: m.SalesScriptScreen })),
);
const ObjectionsScreen = lazyScreen(() =>
  import("./objections-screen.js").then((m) => ({ default: m.ObjectionsScreen })),
);
const DispositionsScreen = lazyScreen(
  () => import("./dispositions-screen.js").then((m) => ({ default: m.DispositionsScreen })),
  SettingsDispositionsSkeleton,
);
const QueueConfigScreen = lazyScreen(
  () => import("./queue-config-screen.js").then((m) => ({ default: m.QueueConfigScreen })),
  QueueConfigSkeleton,
);
const QueueKanbanScreen = lazyScreen(
  () => import("./queue-kanban-screen.js").then((m) => ({ default: m.QueueKanbanScreen })),
  QueueReportSkeleton,
);
const ConversionStagesScreen = lazyScreen(
  () =>
    import("./conversion-stages-screen.js").then((m) => ({ default: m.ConversionStagesScreen })),
  ConversionStagesSkeleton,
);
const AiScriptScreen = lazyScreen(
  () => import("./ai-script-screen.js").then((m) => ({ default: m.AiScriptScreen })),
  AiScriptsSkeleton,
);
const VoicesManagementScreen = lazyScreen(
  () =>
    import("./voices-management-screen.js").then((m) => ({ default: m.VoicesManagementScreen })),
  VoicesSkeleton,
);
const UraScreen = lazyScreen(
  () => import("./ura-screen.js").then((m) => ({ default: m.UraScreen })),
  UraSkeleton,
);

const PlanScreen = lazyScreen(
  () => import("./plan-screen.js").then((m) => ({ default: m.PlanScreen })),
  BillingSkeleton,
);
const BillingScreen = lazyScreen(
  () => import("./billing-screen.js").then((m) => ({ default: m.BillingScreen })),
  BillingSkeleton,
);
const PaymentScreen = lazyScreen(
  () => import("./payment-screen.js").then((m) => ({ default: m.PaymentScreen })),
  PaymentSkeleton,
);

const SettingsBrandScreen = lazyScreen(
  () => import("./settings-brand-screen.js").then((m) => ({ default: m.SettingsBrandScreen })),
  SettingsSkeleton,
);
const SettingsContactScreen = lazyScreen(
  () => import("./settings-contact-screen.js").then((m) => ({ default: m.SettingsContactScreen })),
  SettingsSkeleton,
);
const SettingsHoursScreen = lazyScreen(
  () => import("./settings-hours-screen.js").then((m) => ({ default: m.SettingsHoursScreen })),
  SettingsSkeleton,
);
const SettingsDialerScreen = lazyScreen(
  () => import("./settings-dialer-screen.js").then((m) => ({ default: m.SettingsDialerScreen })),
  SettingsSkeleton,
);
const SettingsClickToCallScreen = lazyScreen(
  () =>
    import("./settings-click-to-call-screen.js").then((m) => ({
      default: m.SettingsClickToCallScreen,
    })),
  SettingsSkeleton,
);
const ProfileScreen = lazyScreen(
  () => import("./profile-screen.js").then((m) => ({ default: m.ProfileScreen })),
  ProfileSkeleton,
);
const InstallAppScreen = lazyScreen(() =>
  import("./install-app-screen.js").then((m) => ({ default: m.InstallAppScreen })),
);

const AutomationsScreen = lazyScreen(() =>
  import("./automations-screen.js").then((m) => ({ default: m.AutomationsScreen })),
);
const McpIntegrationScreen = lazyScreen(
  () => import("./mcp-integration-screen.js").then((m) => ({ default: m.McpIntegrationScreen })),
  McpKeysSkeleton,
);
const CrmIntegrationScreen = lazyScreen(() =>
  import("./crm-integration-screen.js").then((m) => ({ default: m.CrmIntegrationScreen })),
);
const CrmConnectionScreen = lazyScreen(() =>
  import("./crm-connection-screen.js").then((m) => ({ default: m.CrmConnectionScreen })),
);
// Devtools do router só em desenvolvimento. Em produção `import.meta.env.DEV` é
// `false`, o import() some no tree-shaking e não entra no bundle do site.
const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

function RootLayout() {
  const theme = useApplyTheme(); // aplica a classe `dark` no <html> conforme o store de preferências
  useApplyLanguage();
  return (
    <TooltipProvider delayDuration={150}>
      <AnalyticsListeners />
      <Outlet />
      <Toaster theme={theme} />
      <AppUpdateWatcher />
      <Suspense fallback={null}>
        <TanStackRouterDevtools position="bottom-right" />
      </Suspense>
    </TooltipProvider>
  );
}

const rootRoute = createRootRoute({ component: RootLayout });

// /auth — fullscreen, fora do layout com sidebar.
// beforeLoad: quem já tem sessão não deve ver o login — manda direto para o app.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession();
    if (session) {
      throw redirect({ to: "/" });
    }
  },
  component: LoginScreen,
});

// /sign-in — alias de /auth para o loginPage do OAuth MCP (MCP_LOGIN_PAGE default = /sign-in).
// O plugin continua o fluxo de autorização automaticamente após a sessão nascer.
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: LoginScreen,
});

// /signup — cadastro público. Mesmo guard do /auth: quem já tem sessão não tem
// o que fazer aqui. O que vem depois (criar a marca, ver a apresentação) é
// decidido pelo guard do _app, não por esta tela.
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession();
    if (session) {
      throw redirect({ to: "/" });
    }
  },
  component: SignupScreen,
});

// /forgot-password — pede o email e dispara o link de recuperação. Público (sem guard).
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordScreen,
});

// /reset-password — destino do link do email (?token=…). Público: o usuário está
// deslogado quando cai aqui. validateSearch expõe o token/erro vindos na URL.
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: ResetPasswordScreen,
});

// /call?token=… — acesso temporário para ligar, vindo de um produto externo
// (click-to-call). Público: o vendedor NÃO tem sessão do Better Auth quando cai
// aqui; a sessão nasce da troca do token, dentro da tela. Fora do _app de
// propósito — a aba não tem sidebar nem acesso ao resto do app.
const tempCallRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/call",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: TempCallRouteComponent,
});

function TempCallRouteComponent() {
  const { token } = tempCallRoute.useSearch();
  return (
    <Suspense
      fallback={
        <main className="p-6">
          <TempCallRouteSkeleton />
        </main>
      }
    >
      <TempCallScreen token={token} />
    </Suspense>
  );
}

// /click-to-call-hubspot?token=… — mesma tela, endereço próprio. O link vive numa
// propriedade do negócio no HubSpot, então o caminho aparece para o vendedor e
// vale ser auto-explicativo. Multi-uso: recarregar a página NÃO invalida o link.
const hubspotCallRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/click-to-call-hubspot",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: HubspotCallRouteComponent,
});

function HubspotCallRouteComponent() {
  const { token } = hubspotCallRoute.useSearch();
  return (
    <Suspense
      fallback={
        <main className="p-6">
          <TempCallRouteSkeleton />
        </main>
      }
    >
      <TempCallScreen token={token} durable />
    </Suspense>
  );
}

// Flag server-owned (Better Auth additionalField) — não tipada no client, então
// lemos via cast. Sellers criados pelo webhook com senha padrão vêm com true.
function mustChangePassword(session: { user: unknown } | null | undefined): boolean {
  if (!session) return false;
  return (session.user as { mustChangePassword?: boolean }).mustChangePassword === true;
}

// /change-password-required — fullscreen, fora do _app (não passa pela sidebar).
// Guard próprio: exige sessão E a flag ligada. Sem sessão → /auth; sem flag (já
// trocou) → /. Fica fora do _app de propósito, senão o guard do _app redirecionaria
// de volta para cá em loop.
const changePasswordRequiredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/change-password-required",
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    if (!mustChangePassword(session)) {
      throw redirect({ to: "/" });
    }
  },
  component: ChangePasswordRequiredScreen,
});

// /onboarding/organization — nomear a empresa, obrigatório para quem se
// cadastrou. Fullscreen, fora do _app pelo mesmo motivo da tela de senha:
// dentro do layout, o guard mandaria de volta para cá em loop. Guard próprio:
// sem sessão → /auth; passo JÁ concluído → / (ele não se repete).
//
// Quem decide "já concluiu" é `companyOnboarded`, NÃO `hasOrganization`. Desde
// o cadastro transacional a marca nasce junto com a conta, então
// `hasOrganization` é verdadeiro para todo mundo desde o primeiro segundo —
// usá-lo aqui expulsaria para "/" exatamente quem precisa desta tela, e o guard
// do _app o devolveria para cá: laço de redirect infinito.
const onboardingOrganizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/organization",
  beforeLoad: async () => {
    const { session, hasOrganization, companyOnboarded } = await getAppAccess();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    if (hasOrganization && companyOnboarded) {
      throw redirect({ to: "/" });
    }
  },
  component: OnboardingOrganizationScreen,
});

// Compatibilidade para links antigos: a apresentação foi retirada do primeiro
// acesso e agora a empresa nomeada segue diretamente para a escolha do plano.
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: async () => {
    const { session, hasOrganization, companyOnboarded } = await getAppAccess();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    if (!hasOrganization || !companyOnboarded) {
      throw redirect({ to: "/onboarding/organization" });
    }
    throw redirect({
      to: "/assinatura",
      search: { retorno: undefined, checkout: undefined },
    });
  },
  component: () => null,
});

// /assinatura — o degrau de pagamento, o último do primeiro acesso. Fullscreen,
// fora do _app pelo mesmo motivo dos anteriores: dentro do layout a guarda
// mandaria de volta para cá em loop.
//
// O ?retorno=1 do Stripe é a exceção da própria guarda: ele precisa CHEGAR aqui
// mesmo com a assinatura já ativa. Sem essa exceção, o redirect para "/" na
// volta do pagamento atropelaria a tela de confirmação — e quem pagou não teria
// como saber se deu certo.
const subscriptionGateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assinatura",
  // Coerção, não guarda de tipo: o parser do TanStack converte ?retorno=1 no
  // NÚMERO 1, e um typeof === "string" descartava o parâmetro em silêncio.
  // Ver lib/subscription-search.
  validateSearch: (search: Record<string, unknown>) => lerBuscaAssinatura(search),
  beforeLoad: async ({ search }) => {
    const { session, hasOrganization, companyOnboarded, subscription } = await getAppAccess();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    // Nomear a empresa vem antes de pagar — mesma ordem do guard do _app. Sem
    // `companyOnboarded` aqui, quem pulasse direto para /assinatura contrataria
    // um plano para uma marca ainda batizada com o nome dele.
    if (!hasOrganization || !companyOnboarded) {
      throw redirect({ to: "/onboarding/organization" });
    }
    if (subscription.active && !search.retorno) {
      throw redirect({ to: "/" });
    }
  },
  component: SubscriptionGateScreen,
});

// /terms e /privacy — páginas legais, públicas e fullscreen (fora do login e da sidebar).
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsScreen,
});
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyScreen,
});

// _app — layout pathless com a sidebar; todas as páginas internas penduram aqui.
// beforeLoad: guard de autenticação. Sem sessão ninguém entra em rota nenhuma daqui,
// nem na raiz — redireciona sempre para /auth. Roda antes de renderizar, então não há
// flash do app deslogado. Quem chega deslogado para conhecer o produto é assunto da
// landing, que mora em disk.lai.ia.br (repositório disk-lp), fora deste app.
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  beforeLoad: async ({ location }) => {
    // O bootstrap valida sessão, marca ativa e papel uma vez. Nas navegações
    // seguintes o snapshot é imediato e revalida em segundo plano quando velho,
    // evitando uma cascata de requests bloqueantes a cada clique na sidebar.
    // Se o serviço de sessão não responde o erro sobe: ele significa app
    // indisponível de verdade, e esconder isso atrás de um redirect para /auth
    // deslogaria quem só pegou uma instabilidade.
    const {
      session,
      baseRole,
      catalog,
      permissions,
      crossBrand,
      hasOrganization,
      companyOnboarded,
      subscription,
    } = await getAppAccess();
    const decision = appGuardDecision({
      hasSession: !!session,
      mustChangePassword: mustChangePassword(session),
      hasOrganization,
      companyOnboarded,
      subscriptionActive: subscription.active,
      allowed: canAccess({
        catalog,
        permissions,
        path: location.pathname,
        crossBrand,
      }),
      pathname: location.pathname,
    });
    if (decision.kind === "redirect") {
      throw redirect({ to: decision.to });
    }
    const preferredEntryPaths =
      baseRole === "seller"
        ? (["/dialer", "/dashboard"] as const)
        : (["/dashboard", "/dialer"] as const);
    const entryPath = preferredEntryPaths.find((path) =>
      canAccess({ catalog, permissions, path, crossBrand }),
    );
    // `no-access` só acontece na raiz (ver app-guard.ts): o papel do usuário não
    // abre nem o app — em vez de um redirect para "/" (loop), a própria raiz
    // mostra o estado explícito. Sem Home, a raiz encaminha gestores para o
    // Dashboard e vendedores para o Discador, respeitando as permissões.
    return { session, noAccess: decision.kind === "no-access", entryPath };
  },
  component: AppLayoutRoute,
});

// Quem chega aqui tem sessão: o beforeLoad acima já mandou o resto para /auth.
export function AppLayoutRoute() {
  return <AppLayout />;
}

const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/",
  component: IndexRoute,
});

// Exportada (não usada fora deste arquivo) para o router.test.tsx cobrir a
// tradução do contexto do beforeLoad em UI.
export function IndexRoute() {
  const { noAccess, entryPath } = indexRoute.useRouteContext();
  return noAccess || !entryPath ? <NoAccessScreen /> : <Navigate to={entryPath} replace />;
}

// Papel sem "Início → Abrir a tela" (dashboard.root.view): nenhuma rota é
// permitida, nem a raiz. A sidebar/menu do usuário continua disponível (é
// quem dá a saída — sair da conta ou pedir para o diretor ajustar o papel);
// esta tela só evita o app em branco/loop.
export function NoAccessScreen() {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-[60vh] flex-col items-center justify-center gap-2 p-6 text-center">
      <h1 className="text-lg font-semibold tracking-tight">{t("noAccess.title")}</h1>
      <p className="max-w-md text-sm text-muted-foreground">{t("noAccess.description")}</p>
    </main>
  );
}

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/dashboard",
  component: Dashboard,
});

// Ocultado da navegação e bloqueado por URL direta: redireciona para o dashboard.
// Para reativar, remova o beforeLoad (e reponha o link em app-sidebar/mobile-nav).
const controlDeskRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/control-desk",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
  component: ControlDesk,
});

// Suporte (/support): layout de passagem — a navegação entre Ajuda e Meus
// chamados é só pelo grupo "Suporte" da barra lateral/drawer, ver
// support-layout.tsx. Sem guarda de papel — todo mundo pode ver.
const supportLayoutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/support",
  component: SupportLayout,
});

// Entrada de Suporte: mantém /support compatível, mas leva cada área para uma
// URL própria.
export const supportIndexRedirectRoute = createRoute({
  getParentRoute: () => supportLayoutRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/support/help" });
  },
});

// Central de Ajuda (/support/help): perguntas frequentes e abertura de chamado.
const helpRoute = createRoute({
  getParentRoute: () => supportLayoutRoute,
  path: "/help",
  component: HelpScreen,
});

// Meus chamados (/support/tickets): portal do cliente para o suporte. Sem
// guarda de papel, como /support — o back é quem decide o que cada papel
// enxerga na lista.
const supportTicketsRoute = createRoute({
  getParentRoute: () => supportLayoutRoute,
  path: "/tickets",
  component: SupportTicketsScreen,
});

// Tela do chamado (/support/tickets/$id): o id na URL é o que faz o chamado
// sobreviver ao F5 e caber num link mandado a um colega.
const supportTicketRoute = createRoute({
  getParentRoute: () => supportLayoutRoute,
  path: "/tickets/$id",
  component: function SupportTicketRoute() {
    const { id } = supportTicketRoute.useParams();
    return <SupportTicketScreen id={id} />;
  },
});

// URLs antigas (/help*), em produção há tempo — inclusive compartilhadas como
// link direto para um chamado. Redirecionam para o endereço novo em inglês,
// preservando o $id.
// Exportadas para o router.test.tsx cobrir o redirect diretamente (mesma
// razão de AppLayoutRoute/IndexRoute serem exportadas ali).
export const helpRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/help",
  beforeLoad: () => {
    throw redirect({ to: "/support/help" });
  },
});

export const supportTicketsRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/help/tickets",
  beforeLoad: () => {
    throw redirect({ to: "/support/tickets" });
  },
});

export const supportTicketRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/help/tickets/$id",
  beforeLoad: ({ params }: { params: { id: string } }) => {
    throw redirect({ to: "/support/tickets/$id", params: { id: params.id } });
  },
});

// O assistente ainda está em desenvolvimento. Mantemos estes caminhos
// registrados apenas para que links antigos não caiam em uma página quebrada,
// mas a tela em si fica inacessível até a feature ser liberada.
export const supportAssistantRoute = createRoute({
  getParentRoute: () => supportLayoutRoute,
  path: "/assistant",
  beforeLoad: () => {
    throw redirect({ to: "/support/help" });
  },
});

export const supportAssistantConversationRoute = createRoute({
  getParentRoute: () => supportLayoutRoute,
  path: "/assistant/$conversationId",
  beforeLoad: () => {
    throw redirect({ to: "/support/help" });
  },
});

export const assistantRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/assistant",
  beforeLoad: () => {
    throw redirect({ to: "/support/help" });
  },
});

export const assistantConversationRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/assistant/$conversationId",
  beforeLoad: () => {
    throw redirect({ to: "/support/help" });
  },
});

const journeyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/jornada",
  component: JourneyScreen,
});

const capabilityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/cap/$capabilityId",
  component: CapabilityDetail,
});

// Ocultado da navegação e bloqueado por URL direta: redireciona para o dashboard.
const sobreRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/about",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
  component: Sobre,
});

// Telas vazias (placeholders) — em construção. Path em inglês, label (title) em português.
const ramaisSipRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/monitoring/sip-extensions",
  component: SipAgentsScreen,
});
const troncosRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/monitoring/trunks",
  component: TrunksScreen,
});
const chamadasRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/monitoring/active-calls",
  component: ActiveCallsScreen,
});
const filasAtendimentoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/monitoring/queues",
  component: QueuesScreen,
});

const relLigacoesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/calls",
  component: CallsReportScreen,
});
const relOverviewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports",
  component: ReportsOverviewScreen,
});
const relPredictiveRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/predictive",
  // Busca/status/página da tabela de auditoria (antiga tela
  // /management/predictive-audit) vivem na URL. O período NÃO: quem manda na
  // data é o seletor do topo da tela.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  } => {
    const str = (value: unknown) =>
      typeof value === "string" && value.length > 0 ? value : undefined;
    const num = (value: unknown) => (Number.isFinite(Number(value)) ? Number(value) : undefined);
    return {
      q: str(search.q),
      status: str(search.status),
      page: num(search.page),
      pageSize: num(search.pageSize),
    };
  },
  component: PredictiveReportScreen,
});
const saudeLigacoesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/call-health",
  component: CallHealthScreen,
});
const relFilasRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/queues",
  component: QueueReportScreen,
});
// /reports/cadence — filtros, situação e página na URL (useTableSearch). Tipar
// aqui é o que permite linkar a tela já filtrada ("os atrasados de ontem").
const relCadenciaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/cadence",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    situations?: string;
    outcome?: string;
    sellerId?: string;
    q?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  } => {
    const str = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : undefined);
    const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : undefined);
    return {
      situations: str(search.situations),
      outcome: str(search.outcome),
      sellerId: str(search.sellerId),
      q: str(search.q),
      from: str(search.from),
      to: str(search.to),
      page: num(search.page),
      pageSize: num(search.pageSize),
    };
  },
  component: CadenceReportScreen,
});
const relTabulacoesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/dispositions",
  component: DispositionsReportScreen,
});
const relVendedoresRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/sellers",
  component: SellersReportScreen,
});
const relTodasMarcasRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/all-brands",
  component: AllBrandsReportScreen,
});
// Rota antiga do relatório de agentes: redireciona para /reports/sellers para
// não quebrar links/favoritos existentes.
const relAgentesRedirectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/agents",
  beforeLoad: () => {
    throw redirect({ to: "/reports/sellers" });
  },
});
// Ocultado da navegação e bloqueado por URL direta: redireciona para o dashboard.
const relUrasRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports/ivrs",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
  component: () => <EmptyScreen title={i18n.t("noAccess.ivrReport")} />,
});

const discadorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/dialer",
  component: () => (
    <Suspense fallback={<PhoneWorkspaceSkeleton variant="dialer" withHeader />}>
      <DialerScreen />
    </Suspense>
  ),
});

// Campanhas fora do escopo do MVP: as telas (lista /campaigns e detalhe
// /campaigns/:id) foram escondidas da navegação e as rotas redirecionam para o
// dashboard. A cadência de retentativa segue configurável em
// /management/queue-config (regra de fila, global da org). Para reativar, reponha
// os components (CampaignsScreen/CampaignDetailScreen) e o link na navegação.
const campanhasRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/campaigns",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
});

const campanhaDetalheRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/campaigns/$campaignId",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
});

// A tela de discagem manual (/phone, softphone WebRTC via SIP.js) foi retirada do
// produto: nem menu nem rota. O componente `softphone-screen.tsx` continua no repo,
// sem uso, para o caso de voltar — mas hoje não há URL que o alcance.

const gestaoUsuariosRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/users",
  component: UsersScreen,
});
// /management/roles — Papéis e permissões (papéis customizáveis por marca).
const gestaoRolesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/roles",
  component: RolesScreen,
});
// /management/access — "Monitoramento de acesso". validateSearch tipa a
// paginação e o filtro "somente ativas" que o useTableSearch escreve na URL.
const gestaoAcessoRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/access",
  validateSearch: (
    search: Record<string, unknown>,
  ): { active?: string; page?: number; pageSize?: number } => {
    const n = Number(search.page);
    const s = Number(search.pageSize);
    return {
      active: search.active === "1" ? "1" : undefined,
      page: Number.isFinite(n) ? n : undefined,
      pageSize: Number.isFinite(s) ? s : undefined,
    };
  },
  component: AccessLogScreen,
});
// /management/compliance — central de direitos do titular, retenção e consentimento.
const gestaoComplianceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/compliance",
  component: ComplianceScreen,
});
// /management/webhook-logs — "Logs de integração". validateSearch tipa e sanea os
// filtros/paginação/ordenação que o useTableSearch escreve na URL (linkável).
const gestaoLogsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/webhook-logs",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    outcome?: string;
    source?: string;
    q?: string;
    from?: string;
    to?: string;
    sortBy?: string;
    sortDir?: string;
    page?: number;
    pageSize?: number;
    selected?: string;
  } => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    const num = (v: unknown) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    return {
      outcome: str(search.outcome),
      source: str(search.source),
      q: str(search.q),
      from: str(search.from),
      to: str(search.to),
      sortBy: str(search.sortBy),
      sortDir: str(search.sortDir),
      page: num(search.page),
      pageSize: num(search.pageSize),
      selected: str(search.selected),
    };
  },
  component: WebhookLogsScreen,
});
// Geral do click-to-call: a apresentação da funcionalidade (o que o vendedor vê
// e como incorporar o link). Primeiro item do grupo, antes de Auditoria e Saúde
// — é o que se lê antes de ter número nenhum para olhar.
const gestaoClickToCallOverviewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/click-to-call",
  component: ClickToCallOverviewScreen,
});
const gestaoClickToCallAuditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/click-to-call-audit",
  // A tela guarda filtros, estágio e página na URL (useTableSearch). Tipar aqui
  // é o que permite o "ver casos" da Saúde do click-to-call linkar com search.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    status?: string;
    stage?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  } => {
    const str = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : undefined);
    const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : undefined);
    return {
      q: str(search.q),
      status: str(search.status),
      stage: str(search.stage),
      from: str(search.from),
      to: str(search.to),
      page: num(search.page),
      pageSize: num(search.pageSize),
    };
  },
  component: ClickToCallAuditScreen,
});
const gestaoClickToCallReportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/click-to-call-report",
  component: ClickToCallReportScreen,
});
const gestaoClickToCallHealthRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/click-to-call-health",
  component: ClickToCallHealthScreen,
});
const gestaoCallFormRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/call-form",
  component: CallFormScreen,
});
// IA de voz fora da oferta inicial. As rotas permanecem registradas para que
// a implementação possa voltar sem migração de URL, mas nenhum endereço abre
// as telas enquanto o produto não for comercializado.
export const gestaoAiAvaliacoesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/ai-avaliacoes",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
  component: AiEvaluationsScreen,
});
const gestaoLeadsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/leads",
  // Esta rota chegou a redirecionar para /reports/queues (a gestão de leads
  // parecia caber inteira no relatório da fila). Voltou porque o cadastro
  // manual e a importação por CSV são ações próprias da lista de leads, sem
  // equivalente na fila — /reports/queues continua sendo a visão de ORDEM DE
  // DISCAGEM (quem liga para quem agora), uma pergunta diferente desta tela.
  component: LeadsScreen,
});
const gestaoSalesScriptRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/sales-script",
  component: SalesScriptScreen,
});
const gestaoObjectionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/objections",
  component: ObjectionsScreen,
});
const gestaoDispositionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/dispositions",
  component: DispositionsScreen,
});
// Status do lead virou o primeiro card de /management/queue-config. A rota antiga
// redireciona para não quebrar favoritos.
const gestaoLeadStatusesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/lead-statuses",
  beforeLoad: () => {
    throw redirect({ to: "/management/queue-config" });
  },
});
const gestaoQueueConfigRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/queue-config",
  component: QueueConfigScreen,
});
// Quadro da fila: a mesma fila do Relatório Filas, aberta por etapa. Fica
// em /management (gestor, por access.ts) junto da regra que a produz.
const gestaoQueueKanbanRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/queue-kanban",
  component: QueueKanbanScreen,
});
const gestaoConversionStagesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/conversion-stages",
  component: ConversionStagesScreen,
});
export const gestaoAiScriptRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/ai-script",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
  component: AiScriptScreen,
});
export const gestaoAiScriptConstrutorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/ai-script/construtor/$id",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
  component: () => (
    <Suspense
      fallback={
        <main className="p-6">
          <AiConstructorRouteSkeleton />
        </main>
      }
    >
      <AiScriptConstrutorScreen />
    </Suspense>
  ),
});
export const gestaoVozesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/voices",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
  component: VoicesManagementScreen,
});
const gestaoUraRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/management/ura",
  component: UraScreen,
});
const financialPlanRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/financial/plan",
  component: PlanScreen,
});
const financialConsumptionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/financial/consumption",
  component: BillingScreen,
});
const financialPaymentRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/financial/payment",
  component: PaymentScreen,
});
// Compatibilidade para favoritos antigos, sem manter telas duplicadas no menu.
const legacyFinancialBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/financial/billing",
  beforeLoad: () => {
    throw redirect({ to: "/financial/consumption" });
  },
  component: () => null,
});
const legacyFinancialCostsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/financial/costs",
  beforeLoad: () => {
    throw redirect({ to: "/financial/consumption" });
  },
  component: () => null,
});
const legacyFinancialCostsByUserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/financial/costs-by-user",
  beforeLoad: () => {
    throw redirect({ to: "/financial/consumption" });
  },
  component: () => null,
});

// Configurações — grupo "Configurações" na sidebar, uma rota por assunto. A
// antiga tela única /settings foi quebrada nestas três e não existe mais.
const settingsBrandRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings/brand",
  component: SettingsBrandScreen,
});
const settingsContactRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings/contact",
  component: SettingsContactScreen,
});
// Fuso horário + grade semanal — saiu do card "Atendimento" de /settings/contact.
const settingsHoursRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings/hours",
  component: SettingsHoursScreen,
});
const settingsDialerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings/dialer",
  component: SettingsDialerScreen,
});
// Liga/desliga do click to call na org — ver settings-click-to-call-screen.
const settingsClickToCallRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings/click-to-call",
  component: SettingsClickToCallScreen,
});

// Perfil pessoal — disponível para qualquer usuário autenticado pelo dropdown da conta.
const profileRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/profile",
  component: ProfileScreen,
});

// Instalação do PWA — sem permissão nenhuma: quem está logado pode instalar.
const installRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/install",
  component: InstallAppScreen,
});

// Automações — webhook de saída por marca, disparado na tabulação da ligação.
const integrationsAutomationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/integrations/automations",
  component: AutomationsScreen,
});

// Integração MCP — grupo "Integrações" na sidebar.
const integrationsMcpRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/integrations/mcp",
  component: McpIntegrationScreen,
});

// Integração CRM — vitrine dos CRMs suportados (mesmo grupo "Integrações").
const integrationsCrmRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/integrations/crm",
  component: CrmIntegrationScreen,
});

// Detalhe de um CRM: o wizard de conexão. O passo corrente vive na URL
// (?passo=…) para sobreviver a um refresh e poder ser mandado por link — o
// suporte consegue apontar "abre no passo 3". Sem `passo`, a própria tela
// decide onde abrir (introdução ou, para quem já conectou, a confirmação).
const CRM_STEPS: CrmStep[] = ["intro", "chave", "endpoint", "workflow", "confirmar"];
const integrationsCrmProviderRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/integrations/crm/$provider",
  validateSearch: (search: Record<string, unknown>): { passo?: CrmStep } => {
    const passo = CRM_STEPS.find((s) => s === search.passo);
    return passo ? { passo } : {};
  },
  component: function CrmConnectionRoute() {
    const { provider } = integrationsCrmProviderRoute.useParams();
    const { passo } = integrationsCrmProviderRoute.useSearch();
    const navigate = integrationsCrmProviderRoute.useNavigate();
    return (
      <CrmConnectionScreen
        provider={provider as IntegrationProvider}
        step={passo}
        onStepChange={(step) => void navigate({ search: { passo: step } })}
      />
    );
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  signInRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  changePasswordRequiredRoute,
  onboardingOrganizationRoute,
  onboardingRoute,
  subscriptionGateRoute,
  legacyFinancialBillingRoute,
  legacyFinancialCostsRoute,
  legacyFinancialCostsByUserRoute,
  termsRoute,
  privacyRoute,
  tempCallRoute,
  hubspotCallRoute,
  appLayoutRoute.addChildren([
    indexRoute,
    dashboardRoute,
    controlDeskRoute,
    capabilityRoute,
    supportLayoutRoute.addChildren([
      supportIndexRedirectRoute,
      helpRoute,
      supportTicketsRoute,
      supportTicketRoute,
      supportAssistantRoute,
      supportAssistantConversationRoute,
    ]),
    helpRedirectRoute,
    supportTicketsRedirectRoute,
    supportTicketRedirectRoute,
    assistantRedirectRoute,
    assistantConversationRedirectRoute,
    journeyRoute,
    sobreRoute,
    ramaisSipRoute,
    troncosRoute,
    chamadasRoute,
    filasAtendimentoRoute,
    relLigacoesRoute,
    relOverviewRoute,
    relPredictiveRoute,
    saudeLigacoesRoute,
    relFilasRoute,
    relCadenciaRoute,
    relTabulacoesRoute,
    relVendedoresRoute,
    relTodasMarcasRoute,
    relAgentesRedirectRoute,
    relUrasRoute,
    discadorRoute,
    campanhasRoute,
    campanhaDetalheRoute,
    gestaoUsuariosRoute,
    gestaoRolesRoute,
    gestaoAcessoRoute,
    gestaoComplianceRoute,
    gestaoLogsRoute,
    gestaoClickToCallOverviewRoute,
    gestaoClickToCallAuditRoute,
    gestaoClickToCallReportRoute,
    gestaoClickToCallHealthRoute,
    gestaoLeadsRoute,
    gestaoSalesScriptRoute,
    gestaoObjectionsRoute,
    gestaoDispositionsRoute,
    gestaoLeadStatusesRoute,
    gestaoQueueConfigRoute,
    gestaoQueueKanbanRoute,
    gestaoConversionStagesRoute,
    gestaoAiScriptRoute,
    gestaoAiScriptConstrutorRoute,
    gestaoVozesRoute,
    gestaoUraRoute,
    financialPlanRoute,
    financialConsumptionRoute,
    financialPaymentRoute,
    gestaoCallFormRoute,
    gestaoAiAvaliacoesRoute,
    settingsBrandRoute,
    settingsContactRoute,
    settingsHoursRoute,
    settingsDialerRoute,
    settingsClickToCallRoute,
    profileRoute,
    installRoute,
    integrationsAutomationsRoute,
    integrationsMcpRoute,
    integrationsCrmRoute,
    integrationsCrmProviderRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultErrorComponent: AppErrorScreen,
});

// Type-safety global do router (declaration merging).
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
